/**
 * Modal sobre `<dialog>` nativo.
 *
 * Se usa el elemento del navegador y no un div flotante porque ya resuelve
 * bien lo que suele salir mal a mano: atrapa el foco adentro, lo devuelve al
 * cerrar, pinta el `::backdrop` y cierra con Escape.
 *
 * Dos detalles que sí hay que atender:
 *  - `showModal()` / `close()` se sincronizan con la prop `abierto`, porque el
 *    `<dialog>` tiene estado propio en el DOM.
 *  - Escape cierra el diálogo por su cuenta: si no se avisa al estado de
 *    React, la próxima vez que se pida abrir el mismo modal no pasa nada,
 *    porque el estado creía que seguía abierto.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

export interface PropsModal {
  readonly abierto: boolean;
  readonly titulo: ReactNode;
  /** Segunda línea del encabezado: dni, fecha, empresa — la identidad chica. */
  readonly identidad?: ReactNode;
  /** Pieza a la derecha del encabezado: la pastilla de estado, por ejemplo. */
  readonly insignia?: ReactNode;
  readonly onCerrar: () => void;
  /** `ancho` controla el tamaño máximo; el alto siempre scrollea adentro. */
  readonly ancho?: 'normal' | 'amplio';
  readonly children: ReactNode;
  /** Acciones del pie. Sin esto, el pie no se dibuja. */
  readonly pie?: ReactNode;
}

export function Modal({
  abierto,
  titulo,
  identidad,
  insignia,
  onCerrar,
  ancho = 'normal',
  children,
  pie,
}: PropsModal): React.JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialogo = ref.current;
    if (!dialogo) return;
    if (abierto && !dialogo.open) dialogo.showModal();
    if (!abierto && dialogo.open) dialogo.close();
  }, [abierto]);

  return (
    <dialog
      ref={ref}
      className={`modal modal--${ancho}`}
      aria-label={typeof titulo === 'string' ? titulo : undefined}
      onCancel={(ev) => {
        // Se toma el control del cierre por Escape para que el estado de React
        // quede en sincronía con el DOM.
        ev.preventDefault();
        onCerrar();
      }}
      // Clic en el fondo: el `::backdrop` no recibe eventos, pero el propio
      // <dialog> ocupa toda la pantalla y el contenido está en un hijo.
      onClick={(ev) => {
        if (ev.target === ref.current) onCerrar();
      }}
    >
      <div className="modal-caja">
        <header className="modal-encabezado">
          <div>
            <h2>{titulo}</h2>
            {identidad !== undefined && <span className="identidad">{identidad}</span>}
          </div>
          <div className="insignia">
            {insignia}
            <button type="button" className="plano" onClick={onCerrar}>
              <X size={14} aria-hidden="true" />
              Cerrar
            </button>
          </div>
        </header>

        <div className="modal-cuerpo">{children}</div>

        {pie !== undefined && <footer className="modal-pie">{pie}</footer>}
      </div>
    </dialog>
  );
}
